import { useState } from 'react';
import { AlertTriangle, Maximize2, Clock } from 'lucide-react';
import { BrowserMockup } from './BrowserMockup';
import { initials, timeAgo, type GridSize } from './liveScreenUtils';

export interface ScreenTileData {
  id: string;
  name: string;
  isOnline: boolean;
  isFlagged: boolean;
  currentDomain: string | null;
  currentPageTitle?: string | null;
  currentUrl?: string | null;
  currentEventType: string | null;
  /** Alert/open-alert count shown as a badge */
  alertCount: number;
  /** ISO timestamp for "last active" / "screenshot captured" badge */
  timestampLabel: string | null;
  /** Optional screenshot URL (admin only) */
  screenshotUrl?: string | null;
  /** Whether screenshot contains harmful content (admin only) */
  screenshotIsHarmful?: boolean;
}

interface ScreenTileProps {
  tile: ScreenTileData;
  dm: boolean;
  gridSize: GridSize;
  onClick: () => void;
}

export function ScreenTile({ tile, dm, gridSize, onClick }: ScreenTileProps) {
  const [imgError, setImgError] = useState(false);
  const compact = gridSize === 'small';
  const showScreenshot = tile.screenshotUrl && !imgError;
  const isFlaggedOrHarmful = tile.isFlagged || !!tile.screenshotIsHarmful;

  const borderColor = isFlaggedOrHarmful
    ? 'border-red-500 ring-1 ring-red-500'
    : tile.isOnline
    ? (dm ? 'border-emerald-700' : 'border-emerald-400')
    : (dm ? 'border-slate-700' : 'border-slate-200');

  const headerBg = isFlaggedOrHarmful
    ? (dm ? 'bg-red-900/60' : 'bg-red-50')
    : (dm ? 'bg-slate-800' : 'bg-white');

  return (
    <div
      onClick={onClick}
      className={`group relative rounded-xl border-2 overflow-hidden cursor-pointer transition-all duration-200 hover:scale-[1.02] hover:shadow-lg flex flex-col ${borderColor} ${dm ? 'bg-slate-800' : 'bg-white'}`}
    >
      {/* Screen area */}
      <div className="relative overflow-hidden flex-1" style={{ aspectRatio: '16/10' }}>
        {showScreenshot ? (
          <img
            src={tile.screenshotUrl!}
            alt={`${tile.name}'s screen`}
            className="w-full h-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <BrowserMockup
            domain={tile.currentDomain}
            pageTitle={tile.currentPageTitle}
            url={tile.currentUrl}
            eventType={tile.currentEventType}
            isOnline={tile.isOnline}
            dm={dm}
            compact={compact}
          />
        )}

        {/* Flagged overlay */}
        {isFlaggedOrHarmful && (
          <div className="absolute inset-0 bg-red-900/20 flex items-center justify-center pointer-events-none">
            <div className="bg-red-600/90 rounded-lg px-2 py-1 flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 text-white" />
              <span className="text-xs font-bold text-white">FLAGGED</span>
            </div>
          </div>
        )}

        {/* Alert count badge */}
        {tile.alertCount > 0 && (
          <div className="absolute top-1.5 right-1.5 h-5 w-5 rounded-full bg-red-600 flex items-center justify-center">
            <span className="text-xs font-bold text-white">{tile.alertCount}</span>
          </div>
        )}

        {/* Timestamp badge (last active / screenshot age) */}
        {tile.timestampLabel && !compact && (
          <div className={`absolute bottom-1.5 right-1.5 flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${dm ? 'bg-slate-900/80 text-slate-300' : 'bg-white/90 text-slate-600'}`}>
            {tile.screenshotUrl && <Clock className="h-2.5 w-2.5" />}
            {timeAgo(tile.timestampLabel)}
          </div>
        )}

        {/* Hover expand hint */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
          <Maximize2 className="h-6 w-6 text-white drop-shadow" />
        </div>
      </div>

      {/* Student info bar */}
      <div className={`px-2.5 py-2 flex items-center gap-2 shrink-0 ${headerBg}`}>
        <div className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
          tile.isFlagged ? 'bg-red-600 text-white' : (dm ? 'bg-slate-600 text-slate-200' : 'bg-slate-200 text-slate-700')
        }`}>
          {initials(tile.name)}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-semibold truncate ${dm ? 'text-slate-200' : 'text-slate-800'}`}>{tile.name}</p>
          {!compact && tile.currentDomain && (
            <p className={`text-xs truncate ${dm ? 'text-slate-400' : 'text-slate-400'}`}>{tile.currentDomain}</p>
          )}
        </div>
        <div className="shrink-0">
          <div className={`h-2 w-2 rounded-full ${tile.isOnline ? 'bg-emerald-500' : (dm ? 'bg-slate-600' : 'bg-slate-300')}`} />
        </div>
      </div>
    </div>
  );
}
